import React from 'react';
import { Star, Clock, Calendar } from 'lucide-react';
import { useLazyMovieData } from '../../hooks/useLazyTMDB';

export default function MovieCard({ 
  movie, 
  onDragStart, 
  isDraggable = false, 
  isStarred = false, 
  onToggleStar, 
  showStarButton = true,
  className = '',
  reviewAggregate = null,
  lazyLoad = true // Enable lazy loading by default
}) {
  // Use lazy loading for TMDB data
  const { movieData, elementRef, isLazyLoading } = useLazyMovieData(movie, {
    immediate: !lazyLoad // If lazyLoad is false, load immediately
  });

  const handleDragStart = (e) => {
    if (isDraggable && onDragStart) {
      onDragStart(e, movie);
    }
  };

  const handleStarClick = (e) => {
    e.stopPropagation();
    if (onToggleStar) {
      onToggleStar(movie.id);
    }
  };

  return (
    <div
      ref={elementRef}
      className={`bg-gray-700 border-2 border-orange-600 rounded-lg p-3 hover:bg-gray-600 transition-colors ${
        isDraggable ? 'cursor-move' : 'cursor-pointer'
      } ${className} ${isLazyLoading ? 'opacity-75' : ''}`}
      draggable={isDraggable}
      onDragStart={handleDragStart}
    >
      {/* Star Button */}
      {showStarButton && (
        <div className="flex justify-end mb-2">
          <button
            onClick={handleStarClick}
            className={`p-1 rounded-full transition-colors ${
              isStarred 
                ? 'text-yellow-400 hover:text-yellow-300' 
                : 'text-gray-400 hover:text-yellow-400'
            }`}
          >
            <Star size={16} fill={isStarred ? 'currentColor' : 'none'} />
          </button>
        </div>
      )}

      {/* Movie Info */}
      <div className="space-y-2">
        <h3 className="font-bold text-orange-300 text-sm leading-tight">
          {movieData.title}
          {isLazyLoading && <span className="text-xs text-gray-400 ml-2">⏳</span>}
        </h3>
        
        <div className="flex items-center justify-between text-xs text-orange-200">
          <div className="flex items-center space-x-1">
            <Calendar size={12} />
            <span>{movieData.year}</span>
          </div>
          
          {movieData.runtime && (
            <div className="flex items-center space-x-1">
              <Clock size={12} />
              <span>{movieData.runtime}m</span>
            </div>
          )}
        </div>

        {/* Genre */}
        {movieData.genre && (
          <div className="text-xs text-gray-300">
            {movieData.genre}
          </div>
        )}

        {/* Rating */}
        {movieData.rating && (
          <div className="text-xs font-medium text-orange-400">
            ⭐ {movieData.rating}/10
          </div>
        )}

        {/* Review Aggregate */}
        {reviewAggregate && (
          <div className="text-xs bg-yellow-600 text-white px-2 py-1 rounded mt-1">
            <div className="flex items-center justify-between">
              <span>📝 {reviewAggregate.reviewCount} review{reviewAggregate.reviewCount !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center justify-between text-xs mt-1">
              <span>⚰️ {reviewAggregate.avgOverall}</span>
              <span>😱 {reviewAggregate.avgScariness}</span>
            </div>
          </div>
        )}

        {/* Streaming Services */}
        {movie.streamingServices && movie.streamingServices.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {movie.streamingServices.slice(0, 3).map((service, index) => (
              <span
                key={index}
                className="text-xs bg-purple-600 text-white px-2 py-1 rounded-full"
              >
                {service}
              </span>
            ))}
            {movie.streamingServices.length > 3 && (
              <span className="text-xs text-gray-400">
                +{movie.streamingServices.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
